import { Button, Input, Label, Select } from "@/components/ui";
import {
  INGREDIENT_CATEGORIES,
  INGREDIENT_CATEGORY_LABELS,
  type IngredientCategory,
} from "@/api/types/drink";
import type { IngredientDraft } from "./ingredientDraft";

interface IngredientsEditorProps {
  label: string;
  items: IngredientDraft[];
  onChange: (items: IngredientDraft[]) => void;
  onAdd: () => void;
  disabled?: boolean;
  testIdPrefix: string;
}

/**
 * Repeatable ingredient rows: name, oz per serving, category. Mirrors the
 * quotes feature's LineItemsTable pattern (plain useState draft array with
 * stable keys, not react-hook-form's useFieldArray — simpler for a table of
 * primitive string inputs converted to typed values on submit).
 *
 * Reused for both a drink's main `ingredients` and its `na_recipe` (the NA
 * twin), and for an event menu's custom-drink ingredients — `testIdPrefix`
 * keeps the two editors' rows addressable independently in tests.
 */
export function IngredientsEditor({
  label,
  items,
  onChange,
  onAdd,
  disabled,
  testIdPrefix,
}: IngredientsEditorProps) {
  function updateRow(key: string, patch: Partial<IngredientDraft>) {
    onChange(items.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function removeRow(key: string) {
    onChange(items.filter((row) => row.key !== key));
  }

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-editor`}>
      <Label>{label}</Label>

      <div className="hidden grid-cols-[1fr_100px_140px_40px] gap-2 px-1 sm:grid">
        <span className="text-xs font-medium text-text-secondary">Name</span>
        <span className="text-xs font-medium text-text-secondary">Oz / serving</span>
        <span className="text-xs font-medium text-text-secondary">Category</span>
        <span />
      </div>

      {items.map((row) => (
        <div
          key={row.key}
          data-testid={`${testIdPrefix}-row`}
          className="grid grid-cols-2 items-center gap-2 rounded-md border border-border p-2 sm:grid-cols-[1fr_100px_140px_40px] sm:border-0 sm:p-0"
        >
          <Input
            aria-label={`${label} name`}
            value={row.name}
            placeholder="e.g. Reposado tequila"
            disabled={disabled}
            onChange={(e) => updateRow(row.key, { name: e.target.value })}
            className="col-span-2 sm:col-span-1"
          />
          <Input
            aria-label={`${label} oz per serving`}
            type="number"
            min={0}
            step="0.25"
            value={row.oz}
            disabled={disabled}
            onChange={(e) => updateRow(row.key, { oz: e.target.value })}
          />
          <Select
            aria-label={`${label} category`}
            value={row.category}
            disabled={disabled}
            onChange={(e) => updateRow(row.key, { category: e.target.value as IngredientCategory })}
          >
            {INGREDIENT_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {INGREDIENT_CATEGORY_LABELS[cat]}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => removeRow(row.key)}
            aria-label={`Remove ${label.toLowerCase()} row`}
          >
            ✕
          </Button>
        </div>
      ))}

      <Button type="button" variant="secondary" size="sm" onClick={onAdd} disabled={disabled}>
        Add ingredient
      </Button>
    </div>
  );
}
